import { type DragControls, motion, useDragControls } from "motion/react";
import { createContext, useContext, type PropsWithChildren } from "react";
import { twMerge } from "tailwind-merge";

import { useDragContext } from "../context/DragContext";

const DragItemContext = createContext<DragControls | undefined>(undefined);

export function DragItem({
  row,
  drag = true,
  auto = false,
  className,
  children,
}: PropsWithChildren<{
  row: number;
  drag?: "x" | "y" | boolean;
  auto?: boolean;
  className?: string;
}>) {
  const { dragSource, handleDragStart, handleDragMove, handleDragEnd } = useDragContext();
  const dragControls = useDragControls();

  return (
    <DragItemContext.Provider value={auto || !drag ? undefined : dragControls}>
      <motion.div
        data-drag-item={row}
        dragControls={auto ? undefined : dragControls}
        dragListener={auto}
        drag={drag}
        layout="position"
        dragSnapToOrigin
        className={twMerge(className, auto && drag && "cursor-grab touch-none select-none")}
        whileDrag={{
          zIndex: 10,
          position: dragSource ? "absolute" : "static",
          boxShadow: "0 0.25rem 0.75rem rgb(0 0 0 / 0.25)",
          width: dragSource?.width,
          height: dragSource?.height,
          top: dragSource?.top,
          left: dragSource?.left,
        }}
        style={{ order: row }}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDrag={handleDragMove}
      >
        {children}
      </motion.div>
    </DragItemContext.Provider>
  );
}

export function DragHandle({ className, children }: PropsWithChildren<{ className?: string }>) {
  const dragControls = useContext(DragItemContext);
  return (
    <div
      onPointerDown={(event) => dragControls?.start(event)}
      className={twMerge(dragControls && "cursor-grab touch-none select-none active:cursor-grabbing", className)}
    >
      {children}
    </div>
  );
}
